import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {ActionItem, OptionItem} from "@khanacademy/wonder-blocks-dropdown";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {doNotTranslateYet} from "@khan/i18n";

import BottomSheet from "#components/bottom-sheet/bottom-sheet.tsx";

import DeleteFileModalLauncher from "./delete-file-modal-launcher.tsx";
import FilenameEntryModal from "./filename-entry-modal.tsx";
import SelectMenu from "./select-menu.tsx";
import * as strings from "./strings.tsx";
import {fileCountLimit} from "./tabbed-code-editor.tsx";

import plusCircleIcon from "@phosphor-icons/core/regular/plus-circle.svg";

type Props = {
    // The filenames to choose from.
    filenames: Array<string>;

    // The currently selected filename.
    selectedFilename: string;

    // Called when a filename is selected.
    onSelect: (filename: string) => void;

    // Called when the user creates a new file.
    onCreateFile?: (filename: string) => void;

    // Called when the user renames a file.
    onRename?: (oldFilename: string, newFilename: string) => void;

    // Called when the user deletes a file.
    onDelete?: (filename: string) => void;

    // Called when the menu is closed. If the menu is closed by clicking outside
    // of it, the event is passed to onClose.
    // **NOTE**: if clickOutEvent is not passed, this callback needs to focus
    // the button that opens this menu.
    onClose: (clickOutEvent: Event | null) => void;
};

export default function MobileFileSelect({
    filenames,
    selectedFilename,
    onSelect,
    onCreateFile,
    onRename,
    onDelete,
    onClose,
}: Props): React.ReactElement {
    const initialFocusedButtonIndexRef = React.useRef(
        filenames.indexOf(selectedFilename),
    );
    const [menuKey, setMenuKey] = React.useState(0);
    // updating the menu's key causes it to remount, and it focuses the button
    // at initialFocusedButtonIndexRef.current when it mounts.
    const refocusMenu = () => setMenuKey((key) => key + 1);

    const [state, setState] = React.useState<
        | {mode: "select" | "create-file"}
        | {mode: "manage" | "rename" | "delete"; filename: string}
    >({mode: "select"});
    const stopManaging = () => {
        setState({mode: "select"});
        refocusMenu();
    };

    const numButtons =
        onCreateFile != null ? filenames.length + 1 : filenames.length;
    const buttons = filenames.map((filename, index) => ({
        key: filename,
        render: (ref) => (
            <OptionItem
                ref={ref}
                variant="check"
                role="menuitem"
                label={filename}
                value={filename}
                selected={filename === selectedFilename}
                onClick={() => {
                    initialFocusedButtonIndexRef.current = index;
                    const canManage =
                        (onRename != null || onDelete != null) && index !== 0;
                    if (canManage) {
                        setState({
                            mode: "manage",
                            filename,
                        });
                    } else {
                        onSelect(filename);
                        onClose(null);
                    }
                }}
                horizontalRule={index < numButtons - 1 ? "full-width" : "none"}
            />
        ),
    }));
    if (onCreateFile != null) {
        buttons.push({
            key: "new-file",
            render: (ref) => {
                const newFileButtonIndex = filenames.length;
                const hasReachedFileLimit = filenames.length >= fileCountLimit;

                const newFileButton = (
                    <ActionItem
                        ref={ref}
                        style={{
                            [`:hover:not(:disabled) .${css(
                                styles.newFileIcon,
                            )}`]: {
                                color: color.white,
                            },
                        }}
                        label={doNotTranslateYet`New file`}
                        disabled={hasReachedFileLimit}
                        onClick={() => {
                            initialFocusedButtonIndexRef.current =
                                newFileButtonIndex;
                            setState({mode: "create-file"});
                        }}
                        leftAccessory={
                            <PhosphorIcon
                                icon={plusCircleIcon}
                                size="medium"
                                // we use className instead of style here because the
                                // literal class name is referenced in the styles of the
                                // ActionItem.
                                className={css(styles.newFileIcon)}
                            />
                        }
                    />
                );

                return hasReachedFileLimit ? (
                    <Tooltip
                        content={strings.maxFilesPerProgram(fileCountLimit)}
                    >
                        {newFileButton}
                    </Tooltip>
                ) : (
                    newFileButton
                );
            },
        });
    }

    return (
        <>
            <SelectMenu
                key={menuKey}
                buttons={buttons}
                initialFocusedButtonIndex={initialFocusedButtonIndexRef.current}
                onClose={(clickOutEvent) => {
                    if (clickOutEvent != null && state.mode !== "select") {
                        return;
                    }
                    onClose(clickOutEvent);
                }}
            />
            {state.mode === "manage" && (
                <BottomSheet
                    opened={true}
                    onClose={stopManaging}
                    content={
                        <View style={styles.sheet}>
                            <Button
                                onClick={() => {
                                    onSelect(state.filename);
                                    onClose(null);
                                }}
                            >
                                {doNotTranslateYet`Open`}
                            </Button>
                            {onRename != null && (
                                <Button
                                    kind="secondary"
                                    onClick={() =>
                                        setState({
                                            mode: "rename",
                                            filename: state.filename,
                                        })
                                    }
                                >
                                    {doNotTranslateYet`Rename`}
                                </Button>
                            )}
                            {onDelete != null && (
                                <Button
                                    kind="secondary"
                                    actionType="destructive"
                                    onClick={() =>
                                        setState({
                                            mode: "delete",
                                            filename: state.filename,
                                        })
                                    }
                                >
                                    {doNotTranslateYet`Delete`}
                                </Button>
                            )}
                        </View>
                    }
                />
            )}
            {state.mode === "rename" && (
                <FilenameEntryModal
                    currentFilename={state.filename}
                    filenames={new Set(filenames)}
                    onCancel={stopManaging}
                    onSubmit={(newFilename) => {
                        onRename?.(state.filename, newFilename);
                        stopManaging();
                    }}
                />
            )}
            {state.mode === "delete" && (
                <DeleteFileModalLauncher
                    filename={state.filename}
                    opened={true}
                    onClose={stopManaging}
                    onConfirm={() => {
                        onDelete?.(state.filename);
                        initialFocusedButtonIndexRef.current = Math.max(
                            initialFocusedButtonIndexRef.current - 1,
                            0,
                        );
                    }}
                />
            )}
            {state.mode === "create-file" && (
                <FilenameEntryModal
                    filenames={new Set(filenames)}
                    onCancel={stopManaging}
                    onSubmit={(newFilename) => {
                        onCreateFile?.(newFilename);
                        // The new file will be created where the "New file"
                        // button used to be, so we don't need to update
                        // focusedButtonIndex.
                        stopManaging();
                    }}
                />
            )}
        </>
    );
}

const styles = StyleSheet.create({
    sheet: {
        display: "flex",
        flexDirection: "column",
        gap: spacing.small_12,
    },
    newFileIcon: {
        marginLeft: spacing.large_24,
        color: color.offBlack64,
    },
});
